import React, { useState, useEffect, useContext } from "react";
import { Row, Col, Button, Input, Label, Table, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { AuthContext } from "../../../contexts/AuthContext";
import FotosArticulo from "../../Articulos/Articulos/FotosArticulo";
import DatosOrdenProduccion, { datosOrdenDeArticulo } from "../../Produccion/Cortes/DatosOrdenProduccion";

/**
 * Orden de maquila de un articulo, capturada desde Nuevo Pedido (opcional).
 *
 * Los mismos datos que CortesCreate: maquilero, fecha compromiso, precio de
 * maquila, consumo de tela y, por color y talla, la merma y la CANTIDAD FINAL a
 * producir (se puede escribir directo o salir de la merma). Como el pedido aun
 * no existe, aqui solo se captura: Nuevo Pedido crea la orden al guardar.
 *
 * `valor` / `onListo(orden | null)`:
 *   { proveedores, fechaCompromiso, precioMaquila, consumoRealPrenda, observaciones,
 *     merma, datos: { marca, tela, lavado, departamento, elaboradoPor, autorizadoPor, diseno },
 *     renglones: { "color|talla": { merma, final, sku } } }
 */
const URL_PROVEEDORES = process.env.REACT_APP_URL_PROVEEDORES;
const URL_CORTES = process.env.REACT_APP_URL_CORTES;

const encabezados = () => ({
  headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
});

let cacheMaquileros = null;

// Igual que el back: redondeo a 6 decimales antes del ceil (100 x 1.10 = 110.00000000000001).
const conMerma = (cantidad, merma) =>
  Math.ceil(Number(((Number(cantidad) || 0) * (1 + (Number(merma) || 0) / 100)).toFixed(6)));
const clave = (c) => `${c.colores}|${c.tallas}`;

/** Cantidad final de cada celda: la capturada si alcanza el pedido; si no, con merma. */
export function finalesDeOrden(orden, tc) {
  const salida = {};
  (tc || []).forEach((c) => {
    const cantidad = parseFloat(c.cantidad) || 0;
    if (cantidad <= 0) return;
    const r = (orden && orden.renglones && orden.renglones[clave(c)]) || {};
    const merma = r.merma !== undefined && r.merma !== "" ? r.merma : orden ? orden.merma : 0;
    const final = Number(r.final);
    salida[clave(c)] = Number.isInteger(final) && final >= cantidad ? final : conMerma(cantidad, merma);
  });
  return salida;
}

function OrdenMaquilaArticulo({ isOpen, toggle, articulo, modeloCliente = "", tc = [], valor, onListo }) {
  const { user } = useContext(AuthContext);
  const nombreUsuario = [user && user.nombre, user && user.apellido].filter(Boolean).join(" ");
  const [maquileros, setMaquileros] = useState(cacheMaquileros || []);
  const [orden, setOrden] = useState(null);
  const [cargando, setCargando] = useState(false);

  const celdas = tc.filter((c) => (parseFloat(c.cantidad) || 0) > 0);

  useEffect(() => {
    if (!isOpen || !articulo) return;
    let vigente = true;
    setCargando(true);
    const cargas = [];
    if (!cacheMaquileros) {
      cargas.push(
        axios.get(URL_PROVEEDORES, encabezados()).then((r) => {
          // El maquilero es un proveedor con tipo "Maquilero".
          cacheMaquileros = (r.data || []).filter((p) => p.tipo === "Maquilero" && p.is_active !== "No");
          if (vigente) setMaquileros(cacheMaquileros);
        })
      );
    }
    if (valor) {
      setOrden(valor);
    } else {
      cargas.push(
        Promise.all([
          axios
            .get(`${URL_CORTES}MermaArticulo/${articulo._id}`, encabezados())
            .then((r) => r.data.porcentajeMerma)
            .catch(() => 2),
          datosOrdenDeArticulo(articulo._id, nombreUsuario, modeloCliente),
        ]).then(([merma, datos]) => {
            if (!vigente) return;
            setOrden({
              proveedores: "",
              fechaCompromiso: "",
              precioMaquila: 0,
              consumoRealPrenda: 0,
              observaciones: "NA",
              merma,
              datos,
              renglones: {},
            });
          })
      );
    }
    Promise.all(cargas)
      .catch(() => Swal.fire("Error", "No se pudieron cargar los maquileros", "error"))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, articulo && articulo._id]);

  const cambiar = (campo, v) => setOrden({ ...orden, [campo]: v });
  const cambiarRenglon = (c, campo, v) => {
    const actual = orden.renglones[clave(c)] || {};
    const nuevo = { ...actual, [campo]: v };
    // Al mover la merma, la final se recalcula; al escribir la final, manda ella.
    if (campo === "merma") nuevo.final = conMerma(c.cantidad, v);
    setOrden({ ...orden, renglones: { ...orden.renglones, [clave(c)]: nuevo } });
  };
  const cambiarMermaGeneral = (v) => {
    // La merma general se aplica a todas las celdas (se puede ajustar una por una).
    const renglones = {};
    celdas.forEach((c) => {
      const sku = (orden.renglones[clave(c)] || {}).sku;
      renglones[clave(c)] = { merma: v, final: conMerma(c.cantidad, v), ...(sku !== undefined ? { sku } : {}) };
    });
    setOrden({ ...orden, merma: v, renglones });
  };

  const finales = orden ? finalesDeOrden(orden, celdas) : {};
  const valorFinal = (c) => {
    const r = orden.renglones[clave(c)];
    return r && r.final !== undefined ? r.final : finales[clave(c)];
  };
  const mermaDe = (c) => {
    const r = orden.renglones[clave(c)];
    return r && r.merma !== undefined ? r.merma : orden.merma;
  };

  const faltan = () => {
    if (!orden) return [];
    const f = [];
    if (!orden.proveedores) f.push("Maquilero");
    if (!orden.fechaCompromiso) f.push("Fecha compromiso");
    const bajas = celdas.filter((c) => {
      const v = Number(valorFinal(c));
      return !Number.isInteger(v) || v < (parseFloat(c.cantidad) || 0);
    });
    if (bajas.length) f.push(`cantidad final menor al pedido (${bajas.map((c) => `${c.colorNombre} ${c.tallaNombre}`).join(", ")})`);
    return f;
  };

  const totalPedido = celdas.reduce((t, c) => t + (parseFloat(c.cantidad) || 0), 0);
  const totalFinal = orden ? celdas.reduce((t, c) => t + (Number(valorFinal(c)) || 0), 0) : 0;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        Orden de maquila {articulo ? `${articulo.codigo || ""} ${articulo.descripcion || ""}` : ""}
      </ModalHeader>
      <ModalBody>
        {cargando || !orden ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : !celdas.length ? (
          <h6>Primero captura las piezas de este artículo en el pedido.</h6>
        ) : (
          <>
            <Row>
              <Col md={4}>
                <Label>Maquilero</Label>
                <Input type="select" value={orden.proveedores} onChange={(e) => cambiar("proveedores", e.target.value)}>
                  <option value="">Selecciona</option>
                  {[...maquileros]
                    .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                    .map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.nombre_comercial}
                      </option>
                    ))}
                </Input>
              </Col>
              <Col md={2}>
                <Label>Fecha compromiso</Label>
                <Input type="date" value={orden.fechaCompromiso} onChange={(e) => cambiar("fechaCompromiso", e.target.value)} />
              </Col>
              <Col md={2}>
                <Label>Precio maquila x prenda</Label>
                <Input type="number" value={orden.precioMaquila} onChange={(e) => cambiar("precioMaquila", e.target.value)} />
              </Col>
              <Col md={2}>
                <Label>Consumo tela x prenda</Label>
                <Input
                  type="number"
                  value={orden.consumoRealPrenda}
                  onChange={(e) => cambiar("consumoRealPrenda", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Label>% Merma general</Label>
                <Input type="number" value={orden.merma} onChange={(e) => cambiarMermaGeneral(e.target.value)} />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={12}>
                <Label>Observaciones</Label>
                <Input value={orden.observaciones} onChange={(e) => cambiar("observaciones", e.target.value)} />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={12}>
                <Label>Fotos del artículo (todas salen en la Orden de Producción)</Label>
                <FotosArticulo id={articulo && articulo._id} tamano={60} />
              </Col>
            </Row>
            <h6 className="mt-3">Datos de la Orden de Producción</h6>
            <DatosOrdenProduccion valor={orden.datos} onChange={(d) => cambiar("datos", d)} />
            <Table size="sm" bordered className="mt-3">
              <thead>
                <tr>
                  <th>Color</th>
                  <th>Talla</th>
                  <th className="text-right">Pedido</th>
                  <th style={{ width: 120 }}>% Merma</th>
                  <th style={{ width: 150 }}>Cantidad final</th>
                  <th style={{ width: 170 }}>SKU</th>
                </tr>
              </thead>
              <tbody>
                {celdas.map((c) => (
                  <tr key={clave(c)}>
                    <td>{c.colorNombre}</td>
                    <td>{c.tallaNombre}</td>
                    <td className="text-right">{c.cantidad}</td>
                    <td>
                      <Input bsSize="sm" type="number" value={mermaDe(c)} onChange={(e) => cambiarRenglon(c, "merma", e.target.value)} />
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        type="number"
                        min={c.cantidad}
                        value={valorFinal(c)}
                        invalid={!(Number(valorFinal(c)) >= (parseFloat(c.cantidad) || 0))}
                        onChange={(e) => cambiarRenglon(c, "final", e.target.value)}
                      />
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        placeholder="Código de barras"
                        value={(orden.renglones[clave(c)] || {}).sku || ""}
                        onChange={(e) => cambiarRenglon(c, "sku", e.target.value.replace(/\D/g, ""))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={2}>Total</th>
                  <th className="text-right">{totalPedido}</th>
                  <th></th>
                  <th>{totalFinal} a producir</th>
                  <th></th>
                </tr>
              </tfoot>
            </Table>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {faltan().length ? (
          <span className="text-muted mr-auto" style={{ fontSize: 13 }}>
            Falta: {faltan().join(", ")}
          </span>
        ) : null}
        {valor ? (
          <Button
            color="danger"
            onClick={() => {
              onListo(null);
              toggle();
            }}
          >
            Quitar orden
          </Button>
        ) : null}
        <Button color="light" onClick={toggle}>
          Cancelar
        </Button>
        <Button
          color="success"
          disabled={!orden || !celdas.length || faltan().length > 0}
          onClick={() => {
            onListo(orden);
            toggle();
          }}
        >
          <i className="fas fa-save" /> Listo
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default OrdenMaquilaArticulo;
